import axiosInstance from '../utils/axiosInstance';

/**
 * Send an invitation to a professional
 * @param {Object} invitationData - The invitation data
 * @returns {Promise} - Promise with the invitation response
 */
export const sendInvitation = async (invitationData) => {
  try {
    const response = await axiosInstance.post('/invitations', invitationData);
    return response.data;
  } catch (error) {
    console.error('Error sending invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error sending invitation'
    );
  }
};

/**
 * Get all invitations for the current user
 * @param {Object} params - Query parameters (status, type)
 * @returns {Promise} - Promise with the invitations
 */
/**
 * Get upcoming meetings for the current user
 * @returns {Promise} - Promise with the upcoming meetings
 */
export const getUpcomingMeetings = async () => {
  try {
    const response = await axiosInstance.get('/invitations/upcoming-meetings');
    return response.data;
  } catch (error) {
    console.error('Error getting upcoming meetings:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error getting upcoming meetings'
    );
  }
};

export const getMyInvitations = async (params = {}) => {
  try {
    console.log('getMyInvitations called with params:', params);
    console.log('Making request to /invitations with params:', params);
    const response = await axiosInstance.get('/invitations', { params });
    console.log('getMyInvitations response:', response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting invitations:', error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      }
    });
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error fetching invitations'
    );
  }
};

/**
 * Get a specific invitation by ID
 * @param {string} id - The invitation ID
 * @returns {Promise} - Promise with the invitation
 */
export const getInvitation = async (id) => {
  try {
    const response = await axiosInstance.get(`/invitations/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error getting invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error fetching invitation'
    );
  }
};

/**
 * Respond to an invitation (accept/decline)
 * @param {string} id - The invitation ID
 * @param {Object} responseData - The response data (status, responseMessage)
 * @returns {Promise} - Promise with the response
 */
export const respondToInvitation = async (id, responseData) => {
  try {
    const token = localStorage.getItem('token');
    console.log(`Auth token prefix: ${token ? token.substring(0, 10) + '...' : 'none'}`);
    console.log(`Calling API to respond to invitation ${id} with status: ${responseData.status}`);

    // Add retry logic
    let attempts = 0;
    const maxAttempts = 3;
    let lastError = null;

    while (attempts < maxAttempts) {
      try {
        attempts++;
        console.log(`API call attempt ${attempts}/${maxAttempts}`);

        const response = await axiosInstance.put(`/invitations/${id}/respond`, responseData);
        console.log(`API call succeeded on attempt ${attempts}`);
        return response.data;
      } catch (retryError) {
        console.error(`Attempt ${attempts} failed:`, retryError);
        lastError = retryError;

        // Only retry on network errors or 500s
        if (!retryError.response || retryError.response.status === 500) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          // Don't retry for 4xx errors
          break;
        }
      }
    }

    // If we get here, all attempts failed
    console.error('All retry attempts failed');
    throw lastError;
  } catch (error) {
    console.error('Error details from API:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });

    // Special handling for common errors
    if (error.response?.status === 500) {
      console.error('Server error detected, trying alternative approach');

      try {
        // Try a simplified request without responseMessage
        const simplifiedData = { status: responseData.status };
        const response = await axiosInstance.put(`/invitations/${id}/respond`, simplifiedData);
        return response.data;
      } catch (fallbackError) {
        console.error('Fallback approach also failed:', fallbackError);
      }
    }

    // Create a more descriptive error with all relevant information
    const errorMessage =
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error responding to invitation';

    const enhancedError = new Error(errorMessage);
    enhancedError.status = error.response?.status;
    enhancedError.originalError = error;
    enhancedError.responseData = error.response?.data;

    throw enhancedError;
  }
};

/**
 * Cancel an invitation with optional reason
 * @param {string} id - The invitation ID
 * @param {string} reason - Optional cancellation reason
 * @returns {Promise} - Promise with the response
 */
export const cancelInvitation = async (id, reason = '') => {
  try {
    const response = await axiosInstance.put(`/invitations/${id}/cancel`, {
      cancellationReason: reason
    });
    return response.data;
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    throw error; // Throw the original error so the modal can handle it
  }
};

/**
 * Get chat thread for an accepted invitation
 * @param {string} invitationId - The invitation ID
 * @returns {Promise} - Promise with the chat thread
 */
export const getInvitationChatThread = async (invitationId) => {
  try {
    const response = await axiosInstance.get(`/invitations/${invitationId}/chat`);
    return response.data;
  } catch (error) {
    console.error('Error getting invitation chat thread:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error retrieving chat thread'
    );
  }
};

/**
 * Unlock chat for a specific invitation (after payment is processed)
 * @param {string} invitationId - The invitation ID
 * @returns {Promise} - Promise with the unlock response
 */
export const unlockInvitationChat = async (invitationId) => {
  try {
    const response = await axiosInstance.post(`/invitations/${invitationId}/unlock-chat`);
    return response.data;
  } catch (error) {
    console.error('Error unlocking invitation chat:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error unlocking chat'
    );
  }
};

/**
 * Lock chat for a specific invitation (Professional only)
 * @param {string} invitationId - The invitation ID
 * @returns {Promise} - Promise with the lock response
 */
export const lockInvitationChat = async (invitationId) => {
  try {
    const response = await axiosInstance.post(`/invitations/${invitationId}/lock-chat`);
    return response.data;
  } catch (error) {
    console.error(`Error locking invitation chat for ${invitationId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to lock chat'
    };
  }
};

/**
 * Toggle chat access for a specific invitation (Professional only)
 * @param {string} invitationId - The invitation ID
 * @returns {Promise} - Promise with the toggle response
 */
export const toggleInvitationChat = async (invitationId) => {
  try {
    const response = await axiosInstance.post(`/invitations/${invitationId}/toggle-chat`);
    return response.data;
  } catch (error) {
    console.error('Error toggling invitation chat:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error toggling chat access'
    );
  }
};

/**
 * Get pending invitation notifications for current user
 * @returns {Promise} - Promise with the notifications
 */
export const getInvitationNotifications = async () => {
  try {
    const response = await axiosInstance.get('/notifications/invitations');
    return response.data;
  } catch (error) {
    console.error('Error getting invitation notifications:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error fetching notifications'
    );
  }
};

/**
 * Mark an invitation notification as read
 * @param {string} notificationId - The notification ID
 * @returns {Promise} - Promise with the response
 */
export const markInvitationNotificationRead = async (notificationId) => {
  try {
    const response = await axiosInstance.put(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error updating notification'
    );
  }
};

/**
 * Direct test method to respond to an invitation - bypasses complex logic
 * @param {string} invitationId - The invitation ID
 * @param {string} status - The status to set ('accepted' or 'declined')
 * @returns {Promise} - Promise with the response
 */
export const testRespondToInvitation = async (invitationId, status) => {
  try {
    console.log(`DIRECT TEST: Responding to invitation ${invitationId} with status ${status}`);

    const response = await axiosInstance.post('/invitations/test-respond', {
      invitationId,
      status
    });

    console.log('Direct test response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in direct test response:', error);
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Error in direct test response'
    );
  }
};

/**
 * Remove an invitation (professional only, for cleaning up accepted invitations)
 * @param {string} id - The invitation ID
 * @returns {Promise} - Promise with the response
 */
export const removeInvitation = async (id) => {
  try {
    const response = await axiosInstance.put(`/invitations/${id}/remove`);
    return response.data;
  } catch (error) {
    console.error('Error removing invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error removing invitation'
    );
  }
};

// Get the thread ID for a given invitation
export const getInvitationThreadId = async (invitationId) => {
  if (!invitationId) {
    throw new Error('Invitation ID is required');
  }

  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  try {
    const response = await axiosInstance.get(`/invitations/${invitationId}/thread`);

    if (response.data?.success) {
      return { success: true, threadId: response.data.threadId };
    } else {
      return { success: false, message: response.data?.message || 'Could not retrieve thread ID' };
    }
  } catch (error) {
    console.error(`Error getting thread ID for invitation ${invitationId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to retrieve thread ID'
    };
  }
};

/**
 * Accept an invitation (professional response)
 * @param {string} id - The invitation ID
 * @returns {Promise} - Promise with the response
 */
export const acceptInvitation = async (id) => {
  try {
    const response = await axiosInstance.put(`/invitations/${id}/accept`);
    return response.data;
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error accepting invitation'
    );
  }
};

/**
 * Reject an invitation (professional response)
 * @param {string} id - The invitation ID
 * @returns {Promise} - Promise with the response
 */
export const rejectInvitation = async (id) => {
  try {
    const response = await axiosInstance.put(`/invitations/${id}/reject`);
    return response.data;
  } catch (error) {
    console.error('Error rejecting invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error rejecting invitation'
    );
  }
};

/**
 * Reschedule an invitation (professional response)
 * @param {string} id - The invitation ID
 * @param {Object} rescheduleData - The reschedule data (newDate, newTime, reason)
 * @returns {Promise} - Promise with the response
 */
export const rescheduleInvitation = async (id, rescheduleData) => {
  try {
    const response = await axiosInstance.put(`/invitations/${id}/reschedule`, rescheduleData);
    return response.data;
  } catch (error) {
    console.error('Error rescheduling invitation:', error);
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error rescheduling invitation'
    );
  }
};

/**
 * Get professional availability for scheduling
 * @param {string} professionalId - The professional ID
 * @param {Object} options - Query parameters (days, date)
 * @returns {Promise} - Promise with the availability data
 */
export const getProfessionalAvailability = async (professionalId, options = {}) => {
  try {
    console.log('\n📞 === INVITATION SERVICE: API CALL ===');
    console.log('🎯 Calling API:', {
      endpoint: `/invitations/availability/${professionalId}`,
      professionalId: professionalId,
      options: options,
      timestamp: new Date().toISOString()
    });

    const response = await axiosInstance.get(`/invitations/availability/${professionalId}`, {
      params: options
    });

    console.log('📡 API Response Status:', response.status, response.statusText);
    console.log('📡 Response Headers:', response.headers);
    console.log('📡 Response Data Type:', typeof response.data);
    console.log('📡 Response Data Keys:', Object.keys(response.data || {}));

    // Try to safely access response data
    let responseAnalysis = {};
    try {
      responseAnalysis = {
        success: response.data?.success,
        slotsCount: response.data?.data?.availableSlots?.length || 0,
        source: response.data?.data?.source,
        hasData: !!response.data?.data,
        hasSlots: !!response.data?.data?.availableSlots
      };
    } catch (analysisError) {
      console.error('Error analyzing response:', analysisError);
    }

    console.log('📡 Response Analysis:', responseAnalysis);
    console.log('📡 Full Response Data:', JSON.stringify(response.data, null, 2));
    console.log('=======================================\n');
    return response.data;
  } catch (error) {
    console.error('❌ API Error getting professional availability:', {
      professionalId,
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      message: error.message
    });
    throw new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      'Error fetching professional availability'
    );
  }
}; 